import { type FC } from "react";

import {
  type AssessmentCountDataWithNotSubmitted,
  useAssessmentMode,
  useAssessmentPublishActions,
} from "../../../../utils/assessment";

import { SECONDARY_OVERLAY_QUERY_PARAM, SecondaryOverlay } from "components/constants";
import { t } from "i18n/i18n";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { pageSelectors } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";

interface AssessmentGradingLastPanelProps {
  onBack: () => void;
  pageId: string;
  assessedCountData: AssessmentCountDataWithNotSubmitted;
  hideButtons?: boolean;
}

// This is the last panel in the assessment grading workflow. It can only be accessed
// if the assessments haven't been published yet.
export const AssessmentGradingLastPanel: FC<AssessmentGradingLastPanelProps> = ({
  onBack,
  pageId,
  assessedCountData: { count, total, notSubmittedCount },
  hideButtons = false,
}) => {
  const { publishGrades } = useAssessmentPublishActions(pageId);
  const { stopAssessing } = useAssessmentMode(pageId);
  const page = useObjectSelector(
    (s) => pageSelectors.selectById(s, pageId),
    ["assessments_published_at"],
  );

  const handlePublishAndExit = async () => {
    await publishGrades();
    await stopAssessing({ [SECONDARY_OVERLAY_QUERY_PARAM]: SecondaryOverlay.AssessmentSummary });
  };

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-4">
      <div className="text-sm font-normal leading-[1.3125rem]">
        {page?.assessments_published_at
          ? t("submission_assessment.last_panel_description_published", {
              context: count === total ? "all" : "some",
              count,
              total,
              notSubmitted: notSubmittedCount,
            })
          : t("submission_assessment.last_panel_description", {
              context: count === total ? "all" : "some",
              count,
              total,
              notSubmitted: notSubmittedCount,
            })}
      </div>

      {!hideButtons && (
        <>
          <SquareButton
            className="w-full"
            eventName="Assessment - Publish"
            kind="success"
            size="s"
            onClick={handlePublishAndExit}
          >
            {t("assessment.publish_page_assessments")}
          </SquareButton>

          <SquareButton className="w-full" kind="tertiary" size="s" onClick={onBack}>
            {t("assessment.edit_assessments")}
          </SquareButton>

          <SquareButton
            className="w-full"
            kind="tertiary"
            size="s"
            onClick={() => {
              stopAssessing();
            }}
          >
            {t("assessment.finish_later")}
          </SquareButton>
        </>
      )}
    </div>
  );
};
